/**
 * PDFs drawn by pdf.js, in the app on macOS and Windows alike: the card's
 * first-page thumbnail and the preview's stacked pages come from the same
 * renderer, so they match pixel for pixel. The bytes come from
 * `pdf-fetch.ts` for a URL, or from the desktop app for a file linked on
 * this computer; pdf.js reads them in its own worker and draws fonts as
 * canvas paths, never adding them to the app's fonts. A URL that could not
 * be read is not asked again this session unless the user tries again; a
 * linked file is asked each time, since it may be back or located again.
 */
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import {
  localFileProblem,
  readLinkedPdf,
  type LinkRef,
} from "@/desktop/local-files";
import { pdfSourceKey, type PdfSource } from "@/lib/pdf-document";
import { useLocalFileProblemStore } from "@/stores/local-file-problem-store";
import { fetchPdfBytes, type PdfBytes, type PdfReadError } from "./pdf-fetch";

/** Backing pixels per CSS pixel are capped, so a large page stays cheap. */
const MAX_PIXEL_RATIO = 2;

/**
 * pdf.js data served same-origin (`vite-pdfjs-assets.ts`), fetched only
 * while a PDF renders.
 */
function assetUrl(dir: string): string {
  return new URL(
    `${import.meta.env.BASE_URL}pdfjs/${dir}/`,
    window.location.href,
  ).href;
}

/** URLs that could not be read this session, and why, by source key. */
const unreadable = new Map<string, PdfReadError>();

export type PdfDocument = PDFDocumentProxy;
type PdfJs = typeof import("pdfjs-dist");

let pdfjs: Promise<PdfJs> | null = null;

/**
 * pdf.js's legacy build (Safari 16 WebKit on macOS 13) and its worker,
 * loaded on the first PDF only.
 */
function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= Promise.all([
    import("pdfjs-dist/legacy/build/pdf.mjs"),
    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
  ]).then(
    ([library, worker]) => {
      library.GlobalWorkerOptions.workerSrc = worker.default;
      return library as PdfJs;
    },
    (error: unknown) => {
      pdfjs = null;
      throw error;
    },
  );
  return pdfjs;
}

export type OpenedPdf = { pdf: PdfDocument } | { error: PdfReadError };

/**
 * A linked file's bytes from the desktop app, telling the card what the
 * read found (missing, out of reach, or there again).
 */
async function readLinkedPdfBytes(ref: LinkRef): Promise<PdfBytes> {
  const { report } = useLocalFileProblemStore.getState();
  try {
    const bytes = await readLinkedPdf(ref);
    report(ref.linkId, null);
    return { bytes };
  } catch (error) {
    const problem = localFileProblem(error);
    report(ref.linkId, problem);
    return { error: problem };
  }
}

/**
 * The PDF at `source` opened with pdf.js, or why it could not be read; a
 * URL that failed is not fetched again this session (see
 * {@link forgetPdfFailure}). An aborted read is not remembered.
 */
export async function openPdfDocument(
  source: PdfSource,
  signal?: AbortSignal,
): Promise<OpenedPdf> {
  const key = pdfSourceKey(source);
  const known = unreadable.get(key);
  if (known) return { error: known };
  const read =
    source.kind === "url"
      ? await fetchPdfBytes(source.url, signal)
      : await readLinkedPdfBytes({
          linkId: source.linkId,
          frameId: source.frameId,
        });
  if (signal?.aborted) return { error: "failed" };
  if ("error" in read) {
    if (source.kind === "url") unreadable.set(key, read.error);
    return read;
  }
  try {
    const library = await loadPdfJs();
    const pdf = await library.getDocument({
      data: read.bytes,
      enableXfa: false,
      stopAtErrors: false,
      disableFontFace: true,
      cMapUrl: assetUrl("cmaps"),
      cMapPacked: true,
      standardFontDataUrl: assetUrl("standard_fonts"),
      iccUrl: assetUrl("iccs"),
      wasmUrl: assetUrl("wasm"),
    }).promise;
    return { pdf };
  } catch {
    unreadable.set(key, "failed");
    return { error: "failed" };
  }
}

/** Lets the next read of `source` fetch it again (the user's Try again). */
export function forgetPdfFailure(source: PdfSource): void {
  unreadable.delete(pdfSourceKey(source));
}

/** Drops a canvas's backing store at once (WebKit keeps it after removal). */
export function releaseCanvas(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

/** Frees a document and its worker. */
export function closePdfDocument(document: PdfDocument | null): void {
  void document?.loadingTask.destroy();
}

/** Width / height of page `pageNumber` at scale 1. */
export async function pdfPageRatio(
  document: PdfDocument,
  pageNumber: number,
): Promise<number> {
  const viewport = (await document.getPage(pageNumber)).getViewport({
    scale: 1,
  });
  return viewport.width / viewport.height;
}

/**
 * Draws page `pageNumber` into `canvas` at `cssWidth` CSS pixels wide, or
 * narrower so it is at most `maxCssHeight` tall (backing pixels at up to 2×
 * `pixelRatio`). The returned task can be cancelled; its promise settles
 * when the page is drawn.
 */
export async function renderPdfPage(
  document: PdfDocument,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  { pixelRatio = 1, maxCssHeight = Number.POSITIVE_INFINITY } = {},
): Promise<RenderTask> {
  const page = await document.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const ratio = Math.min(MAX_PIXEL_RATIO, Math.max(1, pixelRatio));
  const cssScale = Math.min(cssWidth / base.width, maxCssHeight / base.height);
  const viewport = page.getViewport({ scale: cssScale * ratio });
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  return page.render({ canvas, viewport });
}
